import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../components/ui/ToastProvider';
import { MapPin, Edit2, Trash2, Plus, Check, Loader2 } from 'lucide-react';
import { addressService } from '../../services/address.service';
import type { Address } from '../../services/address.service';

const addressSchema = z.object({
  label: z.string().min(1, 'Label is required'),
  name: z.string().min(2, 'Name is required'),
  phone: z.string().min(8, 'Phone is required'),
  street: z.string().min(5, 'Street address is required'),
  city: z.string().min(2, 'City is required'),
  region: z.string().min(2, 'Region is required'),
  postalCode: z.string().optional(),
  isDefault: z.boolean(),
});

type AddressFormData = z.infer<typeof addressSchema>;

export const AddressBook: React.FC = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      label: '',
      name: '',
      phone: '',
      street: '',
      city: '',
      region: '',
      postalCode: '',
      isDefault: false,
    },
  });

  // Fetch addresses
  const { data: addresses = [], isLoading } = useQuery({
    queryKey: ['addresses'],
    queryFn: addressService.getAll,
  });

  // Create address mutation
  const createMutation = useMutation({
    mutationFn: addressService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
      showToast('Address added successfully', 'success');
      handleCancel();
    },
    onError: () => {
      showToast('Failed to add address', 'error');
    },
  });

  // Update address mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Address> }) =>
      addressService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
      showToast('Address updated successfully', 'success');
      handleCancel();
    },
    onError: () => {
      showToast('Failed to update address', 'error');
    },
  });

  // Delete address mutation
  const deleteMutation = useMutation({
    mutationFn: addressService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
      showToast('Address deleted', 'info');
    },
    onError: () => {
      showToast('Failed to delete address', 'error');
    },
  });

  // Set default mutation
  const setDefaultMutation = useMutation({
    mutationFn: addressService.setDefault,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
      showToast('Default address updated', 'success');
    },
    onError: () => {
      showToast('Failed to update default address', 'error');
    },
  });

  const onSubmit = (data: AddressFormData) => {
    if (editingId) {
      updateMutation.mutate({ id: editingId, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (address: Address) => {
    setEditingId(address.id);
    setIsAdding(true);
    reset({
      label: address.label,
      name: address.name,
      phone: address.phone,
      street: address.street,
      city: address.city,
      region: address.region,
      postalCode: address.postalCode || '',
      isDefault: address.isDefault,
    });
  };

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this address?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSetDefault = (id: number) => {
    setDefaultMutation.mutate(id);
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    reset();
  };

  const isMutating =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    setDefaultMutation.isPending;

  if (isLoading) {
    return (
      <Card>
        <CardTitle className="flex items-center space-x-2">
          <MapPin className="h-5 w-5 text-primary" />
          <span>Address Book</span>
        </CardTitle>
        <CardContent>
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardTitle className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <MapPin className="h-5 w-5 text-primary" />
          <span>Address Book</span>
        </div>
        {!isAdding && (
          <Button variant="secondary" size="sm" onClick={() => setIsAdding(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Address
          </Button>
        )}
      </CardTitle>
      <CardContent>
        {isAdding && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="mb-6 space-y-4 rounded-lg border border-[rgb(var(--border))] p-4"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Label"
                placeholder="Home, Work, etc."
                {...register('label')}
                error={errors.label?.message}
              />
              <Input
                label="Full Name"
                placeholder="Recipient name"
                {...register('name')}
                error={errors.name?.message}
              />
            </div>
            <Input
              label="Phone Number"
              placeholder="+233 20 123 4567"
              {...register('phone')}
              error={errors.phone?.message}
            />
            <Input
              label="Street Address"
              placeholder="123 Main Street"
              {...register('street')}
              error={errors.street?.message}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Input
                label="City"
                placeholder="Accra"
                {...register('city')}
                error={errors.city?.message}
              />
              <Input
                label="Region"
                placeholder="Greater Accra"
                {...register('region')}
                error={errors.region?.message}
              />
              <Input
                label="Postal Code (Optional)"
                placeholder="GA-123"
                {...register('postalCode')}
                error={errors.postalCode?.message}
              />
            </div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                {...register('isDefault')}
                className="rounded border-[rgb(var(--border))]"
              />
              <span className="text-sm">Set as default address</span>
            </label>
            <div className="flex space-x-3">
              <Button type="submit" variant="primary" disabled={isMutating}>
                {isMutating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                {editingId ? 'Update' : 'Add'} Address
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        )}

        <div className="space-y-3">
          {addresses.map((address) => (
            <div
              key={address.id}
              className={`flex items-start justify-between rounded-lg border p-4 transition-all ${
                address.isDefault ? 'border-primary bg-primary/5' : 'border-[rgb(var(--border))]'
              }`}
            >
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <span className="font-medium">{address.label}</span>
                  {address.isDefault && (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      Default
                    </span>
                  )}
                </div>
                <p className="text-sm">{address.name}</p>
                <p className="text-sm text-[rgb(var(--light-secondary))]">{address.phone}</p>
                <p className="text-sm text-[rgb(var(--light-secondary))]">
                  {address.street}, {address.city}, {address.region}
                  {address.postalCode && `, ${address.postalCode}`}
                </p>
              </div>
              <div className="flex space-x-1">
                {!address.isDefault && (
                  <button
                    onClick={() => handleSetDefault(address.id)}
                    className="rounded p-1.5 text-green-500 hover:bg-green-500/10 transition-colors"
                    title="Set as default"
                    disabled={isMutating}
                  >
                    <Check className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={() => handleEdit(address)}
                  className="rounded p-1.5 text-blue-500 hover:bg-blue-500/10 transition-colors"
                  disabled={isMutating}
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(address.id)}
                  className="rounded p-1.5 text-red-500 hover:bg-red-500/10 transition-colors"
                  disabled={isMutating}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}

          {addresses.length === 0 && !isAdding && (
            <p className="text-center text-sm text-[rgb(var(--light-secondary))]">
              No addresses saved. Add your first address to make checkout faster.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};